import { useState, useRef } from "react";
import { Send, Paperclip } from "lucide-react";
import { useChat } from "./ChatContext";

interface Props {
  onSend: (content: string, attachment?: File) => void;
  onSendBatch: (files: File[]) => void;
}

export function ChatInput({ onSend, onSendBatch }: Props) {
  const [text, setText] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);
  const { isQr } = useChat();

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!text.trim()) return;
    onSend(text.trim());
    setText("");
  }

  function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []).slice(0, 10);
    const images = files.filter((f) => f.type.startsWith("image/"));
    const others = files.filter((f) => !f.type.startsWith("image/"));

    // PDF e altro: sempre singoli, path legacy (mai nel batch: evita doppione
    // offerte tra hook rabbithole e _ack_batch_finale, vedi ChatContext).
    for (const f of others) onSend("", f);

    if (images.length >= 2) {
      onSendBatch(images);
    } else if (images.length === 1) {
      onSend("", images[0]);
    }

    e.target.value = "";
  }

  return (
    <form
      onSubmit={handleSubmit}
      data-tour="message-input"
      className="flex items-center gap-2 p-3 border-t border-border bg-card"
    >
      <button
        type="button"
        data-tour="upload-button"
        onClick={() => fileRef.current?.click()}
        className={`${isQr ? "w-11 h-11 flex items-center justify-center" : "p-2"} text-muted-foreground hover:text-text rounded-lg hover:bg-surface transition-colors`}
      >
        <Paperclip size={isQr ? 20 : 18} />
      </button>
      <input ref={fileRef} type="file" multiple accept="image/*,application/pdf" className="hidden" onChange={handleFile} />
      <input
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={isQr ? "Scriva un messaggio…" : "Scrivi un messaggio..."}
        className={`flex-1 bg-surface px-4 py-2.5 rounded-lg ${isQr ? "min-h-11 text-base" : "text-sm"} text-text placeholder:text-muted-foreground outline-none border border-border focus:border-brand`}
      />
      <button
        type="submit"
        className={`${isQr ? "w-11 h-11" : "w-9 h-9"} bg-brand rounded-lg flex items-center justify-center text-white hover:bg-brand-dark transition-colors`}
      >
        <Send size={isQr ? 18 : 16} />
      </button>
    </form>
  );
}
